// Ordem customizada do menu lateral (cross-portal).
//
// Cada portal (student, teacher, company, manager) instancia esse módulo
// com sua própria lista de chaves conhecidas e anchors. A persistência é
// local (localStorage por portal) + server (users.prefs.sidebarOrder.<portal>).
//
// Por que cross-portal: a UX de "arrastar pra reordenar o menu" se aplica a
// qualquer painel — vale a pena DRY. Cada portal tem seu vocabulário de
// chaves, então o módulo é genérico (recebe a config no createSidebarOrder).

import { signal, type Signal } from "@preact/signals";

export type AnchorSpec = "start" | "end" | `after:${string}` | `before:${string}`;

export interface SidebarOrderConfig<K extends string> {
  /** Identificador único do portal — usado pra key do localStorage e do
   *  campo em users.prefs (ex: "student", "teacher"). */
  portalKey: string;
  /** Ordem default de TODAS as chaves possíveis daquele portal. Esse default
   *  é o "código padrão" — usado se nem usuário nem admin tiverem custom. */
  defaultOrder: K[];
  /** Pra cada chave conhecida, onde inserir quando o usuário já tem ordem
   *  customizada mas a chave não está nela. Default = "end". */
  insertAnchors?: Partial<Record<K, AnchorSpec>>;
}

export interface SidebarOrderHandle<K extends string> {
  /** Sinal Preact com a ordem atual (sempre completa, em ordem). */
  signal: Signal<K[]>;
  /** Aplica ordem vinda do server (do usuário). Aceita lista parcial. */
  applyServer: (remote: unknown) => void;
  /** Aplica a ordem default SET PELO ADMIN — vinda de /api/sidebar-defaults.
   *  Só tem efeito se o usuário ainda NÃO tem ordem local customizada
   *  (caso contrário, custom vence). */
  applyPlatformDefault: (remote: unknown) => void;
  /** Ativa sync com /api/user/account (não sincroniza antes disso). */
  enableServerSync: () => void;
  /** Salva nova ordem (local + server). Aceita lista parcial. */
  set: (next: K[]) => void;
  /** Restaura ordem default. */
  reset: () => void;
  /** Devolve `items` na ordem custom — pula items que não existem mais
   *  e empurra items novos não cobertos pelos anchors pro final. */
  applyToItems: <T extends { key: K | string }>(items: T[]) => T[];
}

export function createSidebarOrder<K extends string>(
  config: SidebarOrderConfig<K>
): SidebarOrderHandle<K> {
  const storageKey = `${config.portalKey}.sidebar.order`;
  const knownSet = new Set<string>(config.defaultOrder as string[]);

  function sanitize(raw: unknown[]): K[] {
    const out: K[] = [];
    const seen = new Set<string>();
    for (const k of raw) {
      if (typeof k !== "string") continue;
      if (!knownSet.has(k)) continue;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(k as K);
    }
    return out;
  }

  function fillMissing(saved: K[]): K[] {
    const result = saved.slice();
    for (const key of config.defaultOrder) {
      if (result.includes(key)) continue;
      const anchor = config.insertAnchors?.[key] ?? "end";
      if (anchor === "start") {
        result.unshift(key);
      } else if (anchor === "end") {
        result.push(key);
      } else if (anchor.startsWith("after:")) {
        const target = anchor.slice("after:".length) as K;
        const idx = result.indexOf(target);
        if (idx >= 0) result.splice(idx + 1, 0, key);
        else result.push(key);
      } else if (anchor.startsWith("before:")) {
        const target = anchor.slice("before:".length) as K;
        const idx = result.indexOf(target);
        if (idx >= 0) result.splice(idx, 0, key);
        else result.push(key);
      } else {
        result.push(key);
      }
    }
    return result;
  }

  function readLocal(): K[] | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      return sanitize(parsed);
    } catch {
      return null;
    }
  }

  function writeLocal(order: K[]): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(order));
    } catch {
      /* ignore */
    }
  }

  function initial(): K[] {
    const local = readLocal();
    if (local && local.length > 0) return fillMissing(local);
    return config.defaultOrder.slice();
  }

  const sig: Signal<K[]> = signal<K[]>(initial());
  let syncEnabled = false;

  async function pushToServer(order: K[]): Promise<void> {
    if (!syncEnabled) return;
    try {
      await fetch("/api/user/account", {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        // Aninha por portal — `{ prefs: { sidebarOrder: { student: [...] } } }`
        // permite que portais coexistam sem conflito.
        body: JSON.stringify({
          prefs: { sidebarOrder: { [config.portalKey]: order } }
        })
      });
    } catch {
      /* ignore — local já foi salvo */
    }
  }

  return {
    signal: sig,
    applyServer(remote) {
      // Aceita 2 formatos legados: array direto OU objeto por portal.
      let raw: unknown = remote;
      if (
        remote &&
        typeof remote === "object" &&
        !Array.isArray(remote) &&
        (remote as Record<string, unknown>)[config.portalKey]
      ) {
        raw = (remote as Record<string, unknown>)[config.portalKey];
      }
      if (!Array.isArray(raw)) return;
      const cleaned = sanitize(raw);
      const filled = fillMissing(cleaned);
      sig.value = filled;
      writeLocal(filled);
    },
    applyPlatformDefault(remote) {
      // Só aplica se NÃO houver custom local — caso contrário, custom vence.
      // Tipicamente vem de /api/sidebar-defaults: { [portal]: K[] }.
      if (readLocal() && readLocal()!.length > 0) return;
      let raw: unknown = remote;
      if (
        remote &&
        typeof remote === "object" &&
        !Array.isArray(remote) &&
        (remote as Record<string, unknown>)[config.portalKey]
      ) {
        raw = (remote as Record<string, unknown>)[config.portalKey];
      }
      if (!Array.isArray(raw) || raw.length === 0) return;
      const cleaned = sanitize(raw);
      const filled = fillMissing(cleaned);
      sig.value = filled;
      // NÃO escreve no localStorage — assim o usuário continua sem custom e
      // recebe automaticamente novas mudanças do admin.
    },
    enableServerSync() {
      syncEnabled = true;
    },
    set(next) {
      const filled = fillMissing(sanitize(next));
      sig.value = filled;
      writeLocal(filled);
      void pushToServer(filled);
    },
    reset() {
      const def = config.defaultOrder.slice();
      sig.value = def;
      writeLocal(def);
      void pushToServer(def);
    },
    applyToItems<T extends { key: K | string }>(items: T[]): T[] {
      const order = sig.value;
      const byKey = new Map(items.map((i) => [i.key, i]));
      const out: T[] = [];
      const used = new Set<string>();
      for (const k of order) {
        const item = byKey.get(k);
        if (item) {
          out.push(item);
          used.add(k);
        }
      }
      for (const i of items) {
        if (!used.has(i.key)) out.push(i);
      }
      return out;
    }
  };
}
